/**
 * Every domain converter in the app, in one file.
 *
 * They live together rather than beside the queries that use them because the
 * rule they embody is a whole-codebase rule — build the domain object field by
 * field, never spread the document — and a reviewer checking that rule holds
 * should have one file to read, not fifteen.
 */
import type {
  EventKind,
  LitterStatus,
  OffspringStatus,
  Role,
  Sex,
  ViewScope,
} from '@puppy-doctor/platform';
import {
  Animal,
  HealthEvent,
  Invite,
  Tenant,
  Litter,
  MeasurementEntry,
  type MeasurementReading,
  Member,
  Locale,
  Offspring,
  PhotoSession,
  type PhotoSubject,
  requireMillis,
  SizeGroup,
  Task,
  toMillis,
  VIEW_SCOPES,
} from '@puppy-doctor/platform';

import { firestoreConverter } from '@/api/converter.ts';

const str = (v: unknown, fallback = ''): string => (typeof v === 'string' ? v : fallback);
const num = (v: unknown): number | null => (typeof v === 'number' ? v : null);

/**
 * An array of strings, filtered rather than trusted. A photo list is written by
 * one client and read by another that may be a version behind or ahead, and a
 * single non-string in it would fail the whole document's parse — taking the
 * puppy off the screen because one picture's path was written wrong.
 */
const strings = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((item): item is string => typeof item === 'string') : [];

/**
 * A field handed straight to zod to validate — the enums, where narrowing here
 * would duplicate the schema and then drift from it.
 *
 * This is the ONE place a cast is legitimate in this file, and it is legitimate
 * only because `parseDoc` runs immediately afterwards and rejects the document
 * if the cast was a lie. Do not reach for it for anything zod is not checking.
 */
const checked = <T,>(v: unknown): T => v as T;

/**
 * The exception to `checked` above, and the reason is the difference between a
 * field the document cannot exist without and one it can.
 *
 * A litter's size class is optional: it is null on every litter written before
 * the growth monitor existed, and it may hold a class a later build introduced
 * that this one has never heard of. `checked` there would hand zod a string it
 * rejects, and a rejected parse takes the whole litter off the screen — losing
 * the name, the dates and the puppies over one field that only the growth chart
 * reads. Narrowing to null instead loses the chart and nothing else.
 */
const sizeGroup = (v: unknown): SizeGroup | null =>
  SizeGroup.safeParse(v).data ?? null;

/**
 * The dam's pre-mating weight in kilograms, narrowed rather than trusted, and
 * for the same reason as `sizeGroup` above: a litter whose `damWeightKg` was
 * written as a string, or as zero, must lose the birth-weight classification and
 * not the litter. Null — including on every litter written before this field
 * existed — means the growth model falls back to the litter's own median.
 */
const damWeightKg = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : null;

/**
 * The same tolerance, for a language. An invitation written before this field
 * existed has none, and Polish is the default locale of the product — not a
 * guess so much as the answer that was true when the document was written.
 */
const locale = (v: unknown): Locale => Locale.safeParse(v).data ?? 'pl';

/**
 * What a viewer may see, narrowed rather than trusted — and ALL OF IT when the
 * field is not there at all.
 *
 * The absent case is a membership or an invitation written before scopes
 * existed, and all of them is exactly what such a viewer can see today: reading
 * it as none would take the growth chart off the screen of somebody who has been
 * watching it for weeks, the moment this build ships. The security rules default
 * the same way, so the two cannot disagree — see `canView` in firestore.rules —
 * and migration 002 fills the field in so neither default stays load-bearing.
 *
 * An unknown scope written by a later build is dropped rather than failing the
 * parse: losing one section of a screen beats losing the person off the list.
 */
const viewScopes = (v: unknown): ViewScope[] =>
  Array.isArray(v)
    ? v.filter((item): item is ViewScope => VIEW_SCOPES.includes(item as ViewScope))
    : [...VIEW_SCOPES];

export const tenantConverter = firestoreConverter(Tenant, (id, d) => ({
  id,
  name: str(d.name),
  breed: str(d.breed),
  country: str(d.country) === '' ? null : str(d.country),
  createdBy: str(d.createdBy),
  createdAt: requireMillis(d.createdAt, 'createdAt'),
}));

export const memberConverter = firestoreConverter(Member, (id, d) => ({
  uid: id,
  role: checked<Role>(d.role),
  scopes: viewScopes(d.scopes),
  displayName: str(d.displayName),
  invitedBy: str(d.invitedBy) === '' ? null : str(d.invitedBy),
  joinedAt: requireMillis(d.joinedAt, 'joinedAt'),
}));

export const inviteConverter = firestoreConverter(Invite, (id, d) => ({
  id,
  tenantId: str(d.tenantId),
  tenantName: str(d.tenantName),
  email: str(d.email),
  role: checked<Role>(d.role),
  scopes: viewScopes(d.scopes),
  invitedBy: str(d.invitedBy),
  createdAt: requireMillis(d.createdAt, 'createdAt'),
  expiresAt: requireMillis(d.expiresAt, 'expiresAt'),
  acceptedAt: toMillis(d.acceptedAt),
  locale: locale(d.locale),
  sentAt: toMillis(d.sentAt),
  sendError: str(d.sendError) === '' ? null : str(d.sendError),
}));

export const animalConverter = firestoreConverter(Animal, (id, d) => ({
  id,
  name: str(d.name),
  sex: checked<Sex>(d.sex),
  bornAt: toMillis(d.bornAt),
  registration: str(d.registration) === '' ? null : str(d.registration),
  photoPath: str(d.photoPath) === '' ? null : str(d.photoPath),
  notes: str(d.notes),
}));

export const litterConverter = firestoreConverter(Litter, (id, d) => ({
  id,
  name: str(d.name),
  status: checked<LitterStatus>(d.status),
  bornAt: requireMillis(d.bornAt, 'bornAt'),
  sireId: str(d.sireId) === '' ? null : str(d.sireId),
  damId: str(d.damId) === '' ? null : str(d.damId),
  expectedCount: num(d.expectedCount),
  breed: str(d.breed),
  sizeGroup: sizeGroup(d.sizeGroup),
  damWeightKg: damWeightKg(d.damWeightKg),
  closedAt: toMillis(d.closedAt),
  notes: str(d.notes),
}));

export const offspringConverter = firestoreConverter(Offspring, (id, d) => ({
  id,
  litterId: str(d.litterId),
  name: str(d.name),
  sex: checked<Sex>(d.sex),
  markerColor: str(d.markerColor),
  sequence: typeof d.sequence === 'number' ? d.sequence : 0,
  status: checked<OffspringStatus>(d.status),
  birthWeightGrams: num(d.birthWeightGrams),
  microchip: str(d.microchip) === '' ? null : str(d.microchip),
  photoPath: str(d.photoPath) === '' ? null : str(d.photoPath),
  notes: str(d.notes),
}));

/**
 * One weighing's readings, filtered rather than trusted — and DEDUPED.
 *
 * Filtered for the same reason `strings` above is: a single malformed reading
 * would otherwise fail the whole entry's parse, which takes a litter's entire
 * weighing off the chart because one animal's number was written wrong. Losing
 * one weight is bad; losing seven is worse.
 *
 * Deduped because an animal may appear at most once in an entry and nothing in
 * the schema can say so. The last one wins, matching `readingFor` in
 * platform/src/measurements.ts — the two must agree, or the log and the chart
 * would show different numbers for the same session.
 *
 * A reading with no positive weight is dropped rather than zeroed: zero grams is
 * a point on the chart and an alarm underneath it, and it is not what anyone
 * meant.
 */
const readings = (v: unknown): MeasurementReading[] => {
  if (!Array.isArray(v)) return [];

  const byOffspring = new Map<string, MeasurementReading>();
  for (const item of v) {
    if (typeof item !== 'object' || item === null) continue;
    const d = item as Record<string, unknown>;
    const offspringId = str(d.offspringId);
    const grams = num(d.grams);
    if (offspringId === '' || grams === null || grams <= 0) continue;
    byOffspring.set(offspringId, {
      offspringId,
      grams,
      temperatureC: num(d.temperatureC),
      note: str(d.note),
    });
  }
  return [...byOffspring.values()];
};

export const measurementEntryConverter = firestoreConverter(MeasurementEntry, (id, d) => ({
  id,
  litterId: str(d.litterId),
  recordedAt: requireMillis(d.recordedAt, 'recordedAt'),
  readings: readings(d.readings),
  recordedBy: str(d.recordedBy),
  // Entries written before this field existed have none, and the moment the
  // animals were weighed is the closest true thing there is to say about when
  // it was entered. The migration writes exactly this, for the same reason.
  createdAt: toMillis(d.createdAt) ?? requireMillis(d.recordedAt, 'recordedAt'),
  updatedBy: str(d.updatedBy) === '' ? null : str(d.updatedBy),
  updatedAt: toMillis(d.updatedAt),
}));

/**
 * One photographing's subjects, filtered and deduped — the same treatment
 * `readings` above gets, for the same reason: one malformed entry must not take
 * a whole afternoon's photographs off the timeline.
 *
 * An animal with no paths left after filtering is dropped entirely. "Present
 * with an empty array" and "not photographed" are the same fact, and a subject
 * that renders as a name with nothing under it is worse than no row at all.
 */
const subjects = (v: unknown): PhotoSubject[] => {
  if (!Array.isArray(v)) return [];

  const byOffspring = new Map<string, PhotoSubject>();
  for (const item of v) {
    if (typeof item !== 'object' || item === null) continue;
    const d = item as Record<string, unknown>;
    const offspringId = str(d.offspringId);
    const paths = strings(d.paths);
    if (offspringId === '' || paths.length === 0) continue;
    byOffspring.set(offspringId, { offspringId, paths });
  }
  return [...byOffspring.values()];
};

export const photoSessionConverter = firestoreConverter(PhotoSession, (id, d) => ({
  id,
  litterId: str(d.litterId),
  takenAt: requireMillis(d.takenAt, 'takenAt'),
  paths: strings(d.paths),
  subjects: subjects(d.subjects),
  recordedBy: str(d.recordedBy),
  createdAt: toMillis(d.createdAt) ?? requireMillis(d.takenAt, 'takenAt'),
  updatedBy: str(d.updatedBy) === '' ? null : str(d.updatedBy),
  updatedAt: toMillis(d.updatedAt),
}));

/**
 * One thing to do to a litter.
 *
 * The only narrowing worth a note is the pair at the bottom: `doneAt` and
 * `doneBy` are read together, and a document that carries one without the other
 * is read as outstanding. A task marked done by nobody, or attributed to
 * somebody with no moment attached, is a half-write — and the honest reading of
 * half a write is that the job has not been ticked off yet, which puts it back
 * on the open list where somebody will see it.
 */
export const taskConverter = firestoreConverter(Task, (id, d) => {
  const doneAt = toMillis(d.doneAt);
  const doneBy = str(d.doneBy) === '' ? null : str(d.doneBy);

  return {
    id,
    litterId: str(d.litterId),
    text: str(d.text),
    dueAt: requireMillis(d.dueAt, 'dueAt'),
    doneAt: doneBy === null ? null : doneAt,
    doneBy: doneAt === null ? null : doneBy,
    createdBy: str(d.createdBy),
    createdAt: requireMillis(d.createdAt, 'createdAt'),
  };
});

export const eventConverter = firestoreConverter(HealthEvent, (id, d) => ({
  id,
  kind: checked<EventKind>(d.kind),
  recordedAt: requireMillis(d.recordedAt, 'recordedAt'),
  title: str(d.title),
  detail: str(d.detail),
  product: str(d.product) === '' ? null : str(d.product),
  dueAt: toMillis(d.dueAt),
  completedAt: toMillis(d.completedAt),
  recordedBy: str(d.recordedBy),
}));
